/**
 * EntrepriseSettingsCard — Batch #14.314 : UN seul endroit pour l'entreprise du dossier.
 * Nom, NEQ, adresse, contact, taxes (inscription, n°, taux, code par défaut, fréquence), kilométrage, fonctions portail.
 * Les champs partagés sont aussi écrits dans les réglages du grand livre (en-tête des rapports, calcul des taxes des pièces).
 */
import { useState } from "react";
import axios from "axios";
import { Loader2, Save, Settings2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, Field, inputCls } from "./_shared";
import EntrepriseLogoField from "./EntrepriseLogoField";

const SCOPE_LABEL = { all: "Tout (Dépenses + Kilométrage + Factures)", expenses: "Dépenses seulement", mileage: "Kilométrage seulement" };
const TAX_CODES = [["T", "T — Taxable (TPS + TVQ)"], ["G", "G — TPS seulement (hors Québec)"], ["E", "E — Exonéré"], ["Z", "Z — Non taxable / détaxé"]];
const FREQ = [["monthly", "Mensuelle"], ["quarterly", "Trimestrielle"], ["annual", "Annuelle"]];

function Txt({ s, set, k, label, placeholder, testId }) {
  return <Field label={label}><input className={inputCls} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} data-testid={testId} /></Field>;
}

function Sel({ s, set, k, label, options, testId }) {
  return (
    <Field label={label}>
      <select className={inputCls} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={testId}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );
}

export default function EntrepriseSettingsCard({ masterId, settings, onSaved }) {
  const [s, setS] = useState(settings);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/entrepreneurs/${masterId}/settings`,
        { ...s, km_rate: Number(s.km_rate) || 0, tps_rate: Number(s.tps_rate) || 0, tvq_rate: Number(s.tvq_rate) || 0 }, { headers: authH() });
      toast.success("Réglages Entreprise enregistrés");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="ent-settings-card">
      <div>
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Settings2 className="w-4 h-4 text-teal-700" /> Entreprise & taxes</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">Seul endroit où se règlent le nom, l'adresse et les taxes du dossier — repris en en-tête des rapports et sur les ventes / achats de la tenue de livres.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Txt s={s} set={set} k="business_name" label="Nom de l'entreprise" testId="ent-settings-business" />
        <Txt s={s} set={set} k="neq" label="NEQ" placeholder="1234567890" testId="ent-settings-neq" />
        <Txt s={s} set={set} k="address" label="Adresse" testId="ent-settings-address" />
        <Txt s={s} set={set} k="phone" label="Téléphone" testId="ent-settings-phone" />
        <Txt s={s} set={set} k="email" label="Courriel" testId="ent-settings-email" />
        <Sel s={s} set={set} k="portal_scope" label="Fonctions offertes au client (portail)" options={Object.entries(SCOPE_LABEL)} testId="ent-settings-scope" />
      </div>
      <div className="border-t border-slate-100 pt-3">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Logo de l'entreprise — états financiers & rapports</h4>
        <EntrepriseLogoField masterId={masterId} logoUrl={s.logo_url} onChange={(v) => set("logo_url", v)} />
      </div>
      <div className="border-t border-slate-100 pt-3">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Taxes TPS / TVQ</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Inscrit aux taxes (TPS/TVQ)">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-1.5 cursor-pointer">
              <input type="checkbox" checked={!!s.tax_registered} onChange={(e) => set("tax_registered", e.target.checked)} className="w-4 h-4 accent-teal-700" data-testid="ent-settings-tax-registered" />
              Oui — calculer la TPS/TVQ sur les ventes et achats
            </label>
          </Field>
          <Txt s={s} set={set} k="tps_number" label="N° TPS" placeholder="123456789 RT0001" testId="ent-settings-tps-number" />
          <Txt s={s} set={set} k="tvq_number" label="N° TVQ" placeholder="1234567890 TQ0001" testId="ent-settings-tvq-number" />
          <Field label="Taux TPS (%)"><input type="number" step="0.001" min="0" max="100" className={`${inputCls} font-mono`} value={s.tps_rate ?? 5} onChange={(e) => set("tps_rate", e.target.value)} disabled={!s.tax_registered} data-testid="ent-settings-tps-rate" /></Field>
          <Field label="Taux TVQ (%)"><input type="number" step="0.001" min="0" max="100" className={`${inputCls} font-mono`} value={s.tvq_rate ?? 9.975} onChange={(e) => set("tvq_rate", e.target.value)} disabled={!s.tax_registered} data-testid="ent-settings-tvq-rate" /></Field>
          <Sel s={s} set={set} k="default_tax_code" label="Code de taxes par défaut" options={TAX_CODES} testId="ent-settings-default-tax-code" />
          <Sel s={s} set={set} k="filing_frequency" label="Fréquence de déclaration TPS/TVQ" options={FREQ} testId="ent-settings-frequency" />
          <Field label="Taux kilométrage ($/km)"><input type="number" step="0.01" min="0" className={inputCls} value={s.km_rate} onChange={(e) => set("km_rate", e.target.value)} data-testid="ent-settings-km-rate" /></Field>
        </div>
      </div>
      <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-xs font-semibold disabled:opacity-50" data-testid="ent-settings-save">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
      </button>
    </div>
  );
}
